import { OffthreadVideo, useCurrentFrame, useVideoConfig } from "remotion";
import { ClipOverlay } from "@/components/editor/types";
import { animationTemplates } from "@/components/editor/templates/animation-templates";

/**
 * Interface defining the props for the VideoLayerContent component
 */
interface VideoLayerContentProps {
  /** The overlay configuration object containing video properties and styles */
  overlay: ClipOverlay;
}

/**
 * VideoLayerContent component renders a video layer with animations and styling
 *
 * This component handles:
 * - Video playback using Remotion's OffthreadVideo
 * - Enter/exit animations based on the current frame
 * - Styling including transform, opacity, border radius, etc.
 * - Video timing and volume controls
 *
 * @param props.overlay - Configuration object for the video overlay including:
 *   - src: Video source URL
 *   - videoStartTime: Start time offset for the video
 *   - durationInFrames: Total duration of the overlay
 *   - styles: Object containing visual styling properties and animations
 */
export const VideoLayerContent: React.FC<VideoLayerContentProps> = ({
  overlay,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Calculate if we're in the exit phase (last 30 frames)
  const isExitPhase = frame >= overlay.durationInFrames - 30;

  /**
   * Apply enter animation only during entry phase
   */
  const enterAnimation =
      !isExitPhase && overlay.styles.animation?.enter
        ? animationTemplates[overlay.styles.animation?.enter?.name ?? ""]?.enter?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.enter
          )
        : {};

  /**
   * Apply loop animation only during entry phase
   */
  const loopAnimation = animationTemplates[overlay.styles.animation?.loop?.name ?? ""]?.loop?.(
      frame,
      overlay.durationInFrames,
      fps,
      overlay.styles.animation?.loop
    )
  ?? {};

  /**
   * Apply exit animation only during exit phase
   */
  const exitAnimation =
      isExitPhase && overlay.styles.animation?.exit
        ? animationTemplates[overlay.styles.animation?.exit?.name ?? ""]?.exit?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.exit
          )
        : {};

  const videoStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: overlay.styles.objectFit || "cover",
    opacity: overlay.styles.opacity,
    transform: overlay.styles.transform || "none",
    borderRadius: overlay.styles.borderRadius || "0px",
    filter: overlay.styles.filter || "none",
    boxShadow: overlay.styles.boxShadow || "none",
    border: overlay.styles.border || "none",
    ...(isExitPhase ? exitAnimation : enterAnimation),
    ...loopAnimation
  };

  return (
    <OffthreadVideo
      src={overlay.src}
      startFrom={overlay.videoStartTime || 0}
      style={videoStyle}
      volume={overlay.styles.volume ?? 1}
      playbackRate={overlay?.styles?.speed ?? 1}
    />
  );
};
